// XP calculation utilities with hybrid progression formula
// Levels 1-10: XP needed = level² × 10 + 50 (quadratic growth)
// Levels 11+: XP needed = 1050 + (level - 10) × 150 (linear growth)

/**
 * Calculate total XP needed to reach a specific level
 * Uses hybrid formula: quadratic (1-10), then linear (11+)
 * @param targetLevel The level to calculate XP for
 * @returns Total XP needed from level 1 to reach target level
 */
export function calculateXpForLevel(targetLevel: number): number {
  if (targetLevel <= 1) return 0;
  
  let totalXp = 0;
  for (let i = 1; i < targetLevel; i++) {
    if (i <= 10) {
      // Quadratic formula for levels 1-10
      totalXp += i * i * 10 + 50;
    } else {
      // Linear formula for levels 11+
      totalXp += 1050 + (i - 10) * 150;
    }
  }
  
  return totalXp;
}

/**
 * Calculate XP needed for the next level from current level
 * Uses hybrid formula: quadratic (1-10), then linear (11+)
 * @param currentLevel The current level
 * @returns XP needed to advance to next level
 */
export function calculateXpForNextLevel(currentLevel: number): number {
  if (currentLevel <= 10) {
    // Quadratic formula for levels 1-10
    return currentLevel * currentLevel * 10 + 50;
  } else {
    // Linear formula for levels 11+
    return 1050 + (currentLevel - 10) * 150;
  }
}

/**
 * Calculate current level from total XP
 * @param totalXp Total accumulated XP
 * @returns Current level
 */
export function calculateLevelFromXp(totalXp: number): number {
  let currentLevel = 1;
  
  while (currentLevel < 100) {
    const xpForNextLevel = calculateXpForLevel(currentLevel + 1);
    if (totalXp < xpForNextLevel) {
      return currentLevel;
    }
    currentLevel++;
  }
  
  return 100; // Cap at level 100
}
